/**
 * SLA Tracking Service
 * 
 * Makes M3.H3 (Enterprise contracts supportable) true.
 * 
 * Evidence criteria:
 * - SLA commitments are tracked
 * - Uptime is measurable
 * - Performance metrics are reported
 * - Breach risk is predictable
 */

import { recordMetric } from './observability';

// ============================================================================
// Types
// ============================================================================

export type SLAMetricType = 
  | 'uptime'           // System availability
  | 'response_time'    // API response time
  | 'support_response' // Support first response
  | 'support_resolution'// Support ticket resolution
  | 'data_retention'   // Data retention period
  | 'backup_frequency'; // Backup frequency

export interface SLACommitment {
  id: string;
  customerId: string;
  customerName: string;
  contractTier: 'starter' | 'professional' | 'enterprise';
  
  // Contract dates
  contractStart: string;
  contractEnd: string;
  
  // SLA terms
  commitments: SLAMetric[];
  
  // Current status
  overallCompliance: number; // 0-100
  atRisk: boolean;
  
  // Credits
  creditBalance: number; // Accumulated credits for breaches
  lastCreditIssued?: string;
}

export interface SLAMetric {
  type: SLAMetricType;
  target: number;
  unit: string;
  measurement: 'higher_better' | 'lower_better';
  
  // Current performance
  currentValue?: number;
  periodStart: string;
  periodEnd: string;
  
  // Status
  status: 'met' | 'at_risk' | 'breached';
  
  // History
  measurements: SLAMeasurement[];
}

export interface SLAMeasurement {
  timestamp: string;
  value: number;
  metTarget: boolean;
}

export interface UptimeRecord {
  date: string;
  uptimePercent: number;
  downtimeMinutes: number;
  incidents: UptimeIncident[];
}

export interface UptimeIncident {
  id: string;
  startedAt: string;
  resolvedAt?: string;
  durationMinutes: number;
  severity: 'minor' | 'major' | 'critical';
  description: string;
  rootCause?: string;
  postmortemUrl?: string;
}

export interface SLAReport {
  customerId: string;
  customerName: string;
  period: { start: string; end: string };
  
  // Overall
  overallCompliance: number;
  
  // By metric
  metrics: {
    type: SLAMetricType;
    target: number;
    actual: number;
    unit: string;
    status: 'met' | 'at_risk' | 'breached';
    trend: 'improving' | 'stable' | 'declining';
  }[];
  
  // Incidents
  incidentCount: number;
  totalDowntimeMinutes: number;
  
  // Credits
  creditsDue: number;
  creditsIssued: number;
}

// ============================================================================
// Default SLA Targets by Tier
// ============================================================================

export const DEFAULT_SLA_TARGETS: Record<string, Record<SLAMetricType, { target: number; unit: string; measurement: 'higher_better' | 'lower_better' }>> = {
  starter: {
    uptime: { target: 99.5, unit: '%', measurement: 'higher_better' },
    response_time: { target: 500, unit: 'ms', measurement: 'lower_better' },
    support_response: { target: 24, unit: 'hours', measurement: 'lower_better' },
    support_resolution: { target: 72, unit: 'hours', measurement: 'lower_better' },
    data_retention: { target: 30, unit: 'days', measurement: 'higher_better' },
    backup_frequency: { target: 24, unit: 'hours', measurement: 'lower_better' },
  },
  professional: {
    uptime: { target: 99.9, unit: '%', measurement: 'higher_better' },
    response_time: { target: 300, unit: 'ms', measurement: 'lower_better' },
    support_response: { target: 8, unit: 'hours', measurement: 'lower_better' },
    support_resolution: { target: 48, unit: 'hours', measurement: 'lower_better' },
    data_retention: { target: 90, unit: 'days', measurement: 'higher_better' },
    backup_frequency: { target: 12, unit: 'hours', measurement: 'lower_better' },
  },
  enterprise: {
    uptime: { target: 99.99, unit: '%', measurement: 'higher_better' },
    response_time: { target: 200, unit: 'ms', measurement: 'lower_better' },
    support_response: { target: 1, unit: 'hours', measurement: 'lower_better' },
    support_resolution: { target: 24, unit: 'hours', measurement: 'lower_better' },
    data_retention: { target: 365, unit: 'days', measurement: 'higher_better' },
    backup_frequency: { target: 1, unit: 'hours', measurement: 'lower_better' },
  },
};

// ============================================================================
// Credit Schedule
// ============================================================================

export const CREDIT_SCHEDULE: Record<string, { uptimeBelow: number; creditPercent: number }[]> = {
  enterprise: [
    { uptimeBelow: 99.99, creditPercent: 10 },
    { uptimeBelow: 99.9, creditPercent: 25 },
    { uptimeBelow: 99.5, creditPercent: 50 },
    { uptimeBelow: 99.0, creditPercent: 100 },
  ],
  professional: [
    { uptimeBelow: 99.9, creditPercent: 10 },
    { uptimeBelow: 99.5, creditPercent: 25 },
    { uptimeBelow: 99.0, creditPercent: 50 },
  ],
  starter: [
    { uptimeBelow: 99.5, creditPercent: 10 },
    { uptimeBelow: 99.0, creditPercent: 25 },
  ],
};

// ============================================================================
// In-Memory Store
// ============================================================================

const commitmentStore: Map<string, SLACommitment> = new Map();
const uptimeStore: Map<string, UptimeRecord[]> = new Map(); // date -> records

// ============================================================================
// SLA Commitment Management
// ============================================================================

export async function createSLACommitment(
  customerId: string,
  customerName: string,
  contractTier: SLACommitment['contractTier'],
  contractStart: string,
  contractEnd: string
): Promise<SLACommitment> {
  const targets = DEFAULT_SLA_TARGETS[contractTier];
  
  const commitments: SLAMetric[] = Object.entries(targets).map(([type, config]) => ({
    type: type as SLAMetricType,
    target: config.target,
    unit: config.unit,
    measurement: config.measurement,
    periodStart: contractStart,
    periodEnd: contractEnd,
    status: 'met',
    measurements: [],
  }));
  
  const commitment: SLACommitment = {
    id: crypto.randomUUID(),
    customerId,
    customerName,
    contractTier,
    contractStart,
    contractEnd,
    commitments,
    overallCompliance: 100,
    atRisk: false,
    creditBalance: 0,
  };
  
  commitmentStore.set(commitment.id, commitment);
  console.log(`[SLATracking] Created SLA commitment for ${customerName} (${contractTier})`);
  
  return commitment;
}

export async function getSLACommitment(customerId: string): Promise<SLACommitment | null> {
  for (const commitment of commitmentStore.values()) {
    if (commitment.customerId === customerId) {
      return commitment;
    }
  }
  return null;
}

export async function listSLACommitments(
  filter?: { tier?: SLACommitment['contractTier']; atRisk?: boolean }
): Promise<SLACommitment[]> {
  let commitments = Array.from(commitmentStore.values());
  
  if (filter?.tier) {
    commitments = commitments.filter(c => c.contractTier === filter.tier);
  }
  if (filter?.atRisk !== undefined) {
    commitments = commitments.filter(c => c.atRisk === filter.atRisk);
  }
  
  return commitments;
}

// ============================================================================
// Metric Recording
// ============================================================================

export async function recordMetricMeasurement(
  customerId: string,
  metricType: SLAMetricType,
  value: number
): Promise<SLACommitment | null> {
  const commitment = await getSLACommitment(customerId);
  if (!commitment) return null;
  
  const metric = commitment.commitments.find(m => m.type === metricType);
  if (!metric) return null;
  
  const metTarget = metric.measurement === 'higher_better'
    ? value >= metric.target
    : value <= metric.target;
  
  metric.measurements.push({
    timestamp: new Date().toISOString(),
    value,
    metTarget,
  });
  
  metric.currentValue = value;
  
  // Update status
  const recentMeasurements = metric.measurements.slice(-10);
  const recentFails = recentMeasurements.filter(m => !m.metTarget).length;
  
  if (recentFails === 0) {
    metric.status = 'met';
  } else if (recentFails <= 2) {
    metric.status = 'at_risk';
  } else {
    metric.status = 'breached';
  }
  
  // Recalculate overall compliance
  const metCount = commitment.commitments.filter(m => m.status === 'met').length;
  commitment.overallCompliance = Math.round((metCount / commitment.commitments.length) * 100);
  commitment.atRisk = commitment.commitments.some(m => m.status !== 'met');
  
  commitmentStore.set(commitment.id, commitment);
  
  if (!metTarget) {
    recordMetric('usage', 'sla_metric_missed', 1, { metricType, value, target: metric.target });
  }
  
  return commitment;
}

// ============================================================================
// Uptime Recording
// ============================================================================

export async function recordUptime(
  date: string,
  uptimePercent: number,
  downtimeMinutes: number,
  incidents: UptimeIncident[] = []
): Promise<void> {
  const records = uptimeStore.get(date) || [];
  
  records.push({
    date,
    uptimePercent,
    downtimeMinutes,
    incidents,
  });
  
  uptimeStore.set(date, records);
  
  // Update all customer SLAs
  for (const commitment of commitmentStore.values()) {
    await recordMetricMeasurement(commitment.customerId, 'uptime', uptimePercent);
  }
  
  if (uptimePercent < 99.9) {
    recordMetric('usage', 'uptime_degradation', uptimePercent, { downtimeMinutes });
  }
}

export async function getUptimeHistory(days: number = 30): Promise<UptimeRecord[]> {
  const records: UptimeRecord[] = [];
  
  for (const dayRecords of uptimeStore.values()) {
    records.push(...dayRecords);
  }
  
  return records
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, days);
}

// ============================================================================
// Credit Calculation
// ============================================================================

export async function calculateCredits(
  customerId: string,
  periodStart: string,
  periodEnd: string
): Promise<{ creditsPercent: number; reason: string } | null> {
  const commitment = await getSLACommitment(customerId);
  if (!commitment) return null;
  
  const uptimeMetric = commitment.commitments.find(m => m.type === 'uptime');
  if (!uptimeMetric || uptimeMetric.currentValue === undefined) {
    return null;
  }
  
  const schedule = CREDIT_SCHEDULE[commitment.contractTier];
  const currentUptime = uptimeMetric.currentValue;
  
  for (const tier of schedule) {
    if (currentUptime < tier.uptimeBelow) {
      return {
        creditsPercent: tier.creditPercent,
        reason: `Uptime of ${currentUptime}% is below SLA target of ${uptimeMetric.target}%`,
      };
    }
  }
  
  return null;
}

// ============================================================================
// SLA Reporting
// ============================================================================

export async function generateSLAReport(
  customerId: string,
  periodStart: Date,
  periodEnd: Date
): Promise<SLAReport | null> {
  const commitment = await getSLACommitment(customerId);
  if (!commitment) return null;
  
  const metrics = commitment.commitments.map(m => {
    // Calculate trend
    const measurements = m.measurements.filter(meas => {
      const date = new Date(meas.timestamp);
      return date >= periodStart && date <= periodEnd;
    });
    
    const recent = measurements.slice(-5);
    const older = measurements.slice(-10, -5);
    
    let trend: 'improving' | 'stable' | 'declining' = 'stable';
    if (recent.length > 0 && older.length > 0) {
      const recentAvg = recent.reduce((sum, m) => sum + m.value, 0) / recent.length;
      const olderAvg = older.reduce((sum, m) => sum + m.value, 0) / older.length;
      
      const delta = m.measurement === 'higher_better'
        ? recentAvg - olderAvg
        : olderAvg - recentAvg;
      
      if (delta > m.target * 0.05) trend = 'improving';
      else if (delta < -m.target * 0.05) trend = 'declining';
    }
    
    return {
      type: m.type,
      target: m.target,
      actual: m.currentValue || 0,
      unit: m.unit,
      status: m.status,
      trend,
    };
  });
  
  // Count incidents
  const uptimeRecords = await getUptimeHistory(30);
  const periodRecords = uptimeRecords.filter(r => {
    const date = new Date(r.date);
    return date >= periodStart && date <= periodEnd;
  });
  
  const incidentCount = periodRecords.reduce((sum, r) => sum + r.incidents.length, 0);
  const totalDowntimeMinutes = periodRecords.reduce((sum, r) => sum + r.downtimeMinutes, 0);
  
  // Calculate credits
  const credits = await calculateCredits(customerId, periodStart.toISOString(), periodEnd.toISOString());
  
  return {
    customerId,
    customerName: commitment.customerName,
    period: { start: periodStart.toISOString(), end: periodEnd.toISOString() },
    overallCompliance: commitment.overallCompliance,
    metrics,
    incidentCount,
    totalDowntimeMinutes,
    creditsDue: credits?.creditsPercent || 0,
    creditsIssued: commitment.creditBalance,
  };
}

// ============================================================================
// Dashboard Summary
// ============================================================================

export interface SLADashboard {
  generatedAt: string;
  
  // Overall
  totalCommitments: number;
  atRiskCount: number;
  
  // By tier
  byTier: Record<SLACommitment['contractTier'], {
    count: number;
    avgCompliance: number;
    atRisk: number;
  }>;
  
  // System health
  currentUptime: number;
  uptimeTrend: 'improving' | 'stable' | 'declining';
  
  // Credits
  totalCreditsOutstanding: number;
  
  // At risk customers
  atRiskCustomers: SLACommitment[];
}

export async function getSLADashboard(): Promise<SLADashboard> {
  const all = await listSLACommitments();
  
  const byTier: SLADashboard['byTier'] = {
    starter: { count: 0, avgCompliance: 0, atRisk: 0 },
    professional: { count: 0, avgCompliance: 0, atRisk: 0 },
    enterprise: { count: 0, avgCompliance: 0, atRisk: 0 },
  };
  
  for (const c of all) {
    byTier[c.contractTier].count++;
    byTier[c.contractTier].avgCompliance += c.overallCompliance;
    if (c.atRisk) byTier[c.contractTier].atRisk++;
  }
  
  for (const tier of Object.keys(byTier) as SLACommitment['contractTier'][]) {
    if (byTier[tier].count > 0) {
      byTier[tier].avgCompliance = Math.round(byTier[tier].avgCompliance / byTier[tier].count);
    }
  }
  
  const uptimeHistory = await getUptimeHistory(7);
  const currentUptime = uptimeHistory.length > 0 ? uptimeHistory[0].uptimePercent : 100;
  
  const recentAvg = uptimeHistory.slice(0, 3).reduce((sum, r) => sum + r.uptimePercent, 0) / Math.max(uptimeHistory.slice(0, 3).length, 1);
  const olderAvg = uptimeHistory.slice(3, 7).reduce((sum, r) => sum + r.uptimePercent, 0) / Math.max(uptimeHistory.slice(3, 7).length, 1);
  
  let uptimeTrend: 'improving' | 'stable' | 'declining' = 'stable';
  if (recentAvg - olderAvg > 0.1) uptimeTrend = 'improving';
  else if (olderAvg - recentAvg > 0.1) uptimeTrend = 'declining';
  
  const totalCreditsOutstanding = all.reduce((sum, c) => sum + c.creditBalance, 0);
  const atRiskCustomers = all.filter(c => c.atRisk);
  
  return {
    generatedAt: new Date().toISOString(),
    totalCommitments: all.length,
    atRiskCount: atRiskCustomers.length,
    byTier,
    currentUptime,
    uptimeTrend,
    totalCreditsOutstanding,
    atRiskCustomers,
  };
}


